/**
 * Service Worker Helper for Firebase Cloud Messaging
 * Handles registration, updates, and error recovery
 */

/**
 * Check if the browser supports all required features for push notifications
 */
export const checkBrowserSupport = () => {
  const hasServiceWorker = "serviceWorker" in navigator;
  const hasNotifications = "Notification" in window;
  const hasPushManager = "PushManager" in window;

  return {
    supported: hasServiceWorker && hasNotifications && hasPushManager,
    hasServiceWorker,
    hasNotifications,
    hasPushManager,
    userAgent: navigator.userAgent,
    platform: navigator.platform,
  };
};

/**
 * Get or register the Firebase messaging service worker
 */
export const ensureServiceWorkerRegistered = async () => {
  if (!("serviceWorker" in navigator)) {
    throw new Error("Service workers are not supported in this browser");
  }

  try {
    // Check if already registered
    const existingRegistration =
      await navigator.serviceWorker.getRegistration("/");

    if (existingRegistration) {
      // Check if it's our Firebase service worker
      if (
        existingRegistration.active?.scriptURL?.includes(
          "firebase-messaging-sw.js"
        )
      ) {
        console.info("Firebase service worker already registered");
        return existingRegistration;
      }
    }

    // Register new service worker
    console.info("Registering Firebase service worker...");
    const registration = await navigator.serviceWorker.register(
      "/firebase-messaging-sw.js",
      {
        scope: "/",
        updateViaCache: "none", // Ensure we get updates
      }
    );

    // Wait for it to be ready
    await navigator.serviceWorker.ready;

    // Handle updates
    registration.addEventListener("updatefound", () => {
      console.info("Service worker update found");
      const newWorker = registration.installing;

      newWorker?.addEventListener("statechange", () => {
        if (newWorker.state === "activated") {
          console.info("Service worker updated and activated");
        }
      });
    });

    return registration;
  } catch (error) {
    console.error("Service worker registration failed:", error);

    // Provide specific error messages
    if (error.message?.includes("Failed to register")) {
      throw new Error(
        "Failed to register service worker. Please check if the file exists and is accessible."
      );
    } else if (error.message?.includes("SecurityError")) {
      throw new Error(
        "Service worker registration blocked due to security settings. HTTPS is required."
      );
    } else {
      throw error;
    }
  }
};

/**
 * Unregister all service workers (for cleanup/debugging)
 */
export const unregisterAllServiceWorkers = async () => {
  if (!("serviceWorker" in navigator)) {
    return;
  }

  try {
    const registrations = await navigator.serviceWorker.getRegistrations();

    for (const registration of registrations) {
      await registration.unregister();
      console.info("Unregistered service worker:", registration.scope);
    }
  } catch (error) {
    console.error("Failed to unregister service workers:", error);
  }
};

/**
 * Check if push notifications are blocked at the OS level
 */
export const checkSystemNotificationSettings = async () => {
  if (!("permissions" in navigator)) {
    return { available: false };
  }

  try {
    const result = await navigator.permissions.query({ name: "notifications" });

    return {
      available: true,
      state: result.state, // 'granted', 'denied', or 'prompt'
      canRequest: result.state !== "denied",
    };
  } catch (error) {
    console.warn("Cannot check system notification settings:", error);
    return { available: false };
  }
};

/**
 * Debug helper to log all service worker information
 */
export const debugServiceWorkers = async () => {
  if (!("serviceWorker" in navigator)) {
    console.info("Service workers not supported");
    return;
  }

  try {
    const registrations = await navigator.serviceWorker.getRegistrations();

    console.group("Service Worker Debug Info");
    console.info("Controller:", navigator.serviceWorker.controller);
    console.info("Ready:", await navigator.serviceWorker.ready);
    console.info("Registrations:", registrations);

    registrations.forEach((reg, index) => {
      console.group(`Registration ${index + 1}`);
      console.info("Scope:", reg.scope);
      console.info("Active:", reg.active);
      console.info("Installing:", reg.installing);
      console.info("Waiting:", reg.waiting);
      console.info("Update state:", reg.updateViaCache);
      console.groupEnd();
    });

    console.groupEnd();
  } catch (error) {
    console.error("Failed to debug service workers:", error);
  }
};
